import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * StaffProfileExtras
 * ------------------
 * The Phase M gap-closure panel that plugs into the existing
 * "Add / Edit Staff Profile" modal in AdminDashboard.js.
 *
 * It renders 6 collapsible sections:
 *   1. Employment lifecycle (start date, probation end, notice period)
 *   2. Job info (job title, line manager, assigned room)
 *   3. HMRC onboarding (DOB, nationality, sex, starter declaration,
 *      student loan plan, payroll id)
 *   4. Emergency contact (admin-managed — separate from staff-self)
 *   5. Custom competencies (per-org list from /api/custom-competencies)
 *   6. History timelines (salary_history + employment_events)
 *   7. Documents panel (upload/list/delete)
 *   8. Danger zone (Archive/Leaver + Restore)
 *
 * DESIGN:
 *   - Uncontrolled `<input name="…" />` where possible so the values
 *     land in the parent `<form>`'s FormData automatically, matching
 *     the existing modal's submit pattern.
 *   - Documents + Custom Competencies + Archive/Restore manage their
 *     own state and hit the API directly (they are async side-effects,
 *     not part of the profile POST/PUT).
 *
 * Props:
 *   - staff:      the editingStaff object (null for CREATE mode; some
 *                 sections hide when there's no _id yet).
 *   - users:      array of org users for the line-manager dropdown.
 *   - rooms:      array of rooms for the assigned-room dropdown.
 *   - onArchived: called after a successful archive so the parent can
 *                 refresh the list and close the modal.
 *   - onRestored: called after a successful restore so the parent can
 *                 refresh the list.
 *
 * Custom competency selections are tracked in local state and injected
 * back into the parent's form via a hidden JSON input named
 * `custom_competencies_json` (the parent handler parses it).
 */
export function StaffProfileExtras({ staff, users = [], rooms = [], onArchived, onRestored }) {
  const staffId = staff?._id;

  // --- Section visibility (start with essentials expanded) ---
  const [openSection, setOpenSection] = useState('employment');
  const toggle = (name) => setOpenSection((s) => (s === name ? null : name));

  // --- Custom competencies (loaded from server) ---
  const [customCompetencies, setCustomCompetencies] = useState([]);
  const [selectedCustom, setSelectedCustom] = useState(
    (staff?.custom_competencies || []).slice()
  );
  useEffect(() => {
    api.listCustomCompetencies().then(setCustomCompetencies).catch(() => setCustomCompetencies([]));
  }, []);
  useEffect(() => {
    setSelectedCustom((staff?.custom_competencies || []).slice());
  }, [staff?._id]);

  // --- Documents (loaded from server for edit-mode staff) ---
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    document_type: 'dbs',
    issue_date: '',
    expiry_date: '',
    notes: '',
  });
  const [uploadFile, setUploadFile] = useState(null);
  const reloadDocs = useCallback(async () => {
    if (!staffId) return;
    setDocsLoading(true);
    try {
      const docs = await api.listStaffDocuments(staffId);
      setDocuments(docs);
    } catch (err) {
      toast.error('Failed to load documents: ' + err.message);
    } finally {
      setDocsLoading(false);
    }
  }, [staffId]);
  useEffect(() => { reloadDocs(); }, [reloadDocs]);

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Choose a file first');
      return;
    }
    setUploading(true);
    try {
      await api.uploadStaffDocument(staffId, {
        file: uploadFile,
        document_type: uploadForm.document_type,
        issue_date: uploadForm.issue_date || undefined,
        expiry_date: uploadForm.expiry_date || undefined,
        notes: uploadForm.notes || undefined,
      });
      toast.success('Document uploaded');
      setUploadFile(null);
      setUploadForm({ document_type: 'dbs', issue_date: '', expiry_date: '', notes: '' });
      // Clear the file input element too.
      const fileInput = document.querySelector('[data-testid="staff-doc-file"]');
      if (fileInput) fileInput.value = '';
      await reloadDocs();
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };
  const handleDeleteDoc = async (docId, label) => {
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await api.deleteStaffDocument(staffId, docId);
      toast.success('Document deleted');
      await reloadDocs();
    } catch (err) {
      toast.error('Delete failed: ' + err.message);
    }
  };

  // --- Archive (leaver) modal ---
  const [leaverForm, setLeaverForm] = useState({
    open: false,
    leaver_reason: 'resignation',
    leaver_reason_notes: '',
    last_working_day: new Date().toISOString().slice(0, 10),
    is_rehire_eligible: true,
    submitting: false,
  });
  const submitArchive = async () => {
    setLeaverForm((f) => ({ ...f, submitting: true }));
    try {
      await api.archiveStaffProfile(staffId, {
        leaver_reason: leaverForm.leaver_reason,
        leaver_reason_notes: leaverForm.leaver_reason_notes || undefined,
        last_working_day: leaverForm.last_working_day,
        is_rehire_eligible: leaverForm.is_rehire_eligible,
      });
      toast.success('Staff member archived');
      setLeaverForm((f) => ({ ...f, open: false, submitting: false }));
      if (onArchived) onArchived();
    } catch (err) {
      toast.error('Archive failed: ' + err.message);
      setLeaverForm((f) => ({ ...f, submitting: false }));
    }
  };
  const submitRestore = async () => {
    const restart = window.prompt('Rehire from what date? (YYYY-MM-DD, blank = today)', '');
    if (restart === null) return; // user cancelled
    try {
      await api.restoreStaffProfileWithHistory(staffId, restart ? { restart_date: restart } : {});
      toast.success('Staff member restored');
      if (onRestored) onRestored();
    } catch (err) {
      toast.error('Restore failed: ' + err.message);
    }
  };

  // --- Small style helpers to keep the JSX readable ---
  const sectionHeader = (id, label, badge = null) => (
    <button
      type="button"
      className="btn btn-ghost"
      onClick={() => toggle(id)}
      style={{
        width: '100%', justifyContent: 'space-between', display: 'flex',
        padding: '0.55rem 0.75rem', marginTop: '0.6rem',
        background: openSection === id ? 'hsl(var(--muted))' : 'transparent',
        border: '1px solid hsl(var(--border))', borderRadius: 8,
        fontWeight: 600,
      }}
      data-testid={`staff-extras-section-${id}`}
    >
      <span>{label} {badge}</span>
      <span style={{ fontSize: '0.85em', opacity: 0.6 }}>{openSection === id ? '▾' : '▸'}</span>
    </button>
  );

  const twoCol = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem' };
  const inputWrap = { margin: 0 };
  const labelSm = { fontSize: '0.78rem', fontWeight: 600, marginBottom: 4, display: 'block' };
  const inputStyle = { width: '100%', padding: '0.4rem 0.55rem', borderRadius: 6, border: '1px solid hsl(var(--border))', fontSize: '0.85rem' };

  return (
    <div style={{ marginTop: '1rem', borderTop: '1px solid hsl(var(--border))', paddingTop: '0.75rem' }} data-testid="staff-profile-extras">
      {/* Hidden field so parent FormData picks up the multi-select. */}
      <input type="hidden" name="custom_competencies_json" value={JSON.stringify(selectedCustom)} />

      {/* ============================================================
          1. Employment lifecycle
         ============================================================ */}
      {sectionHeader('employment', '📅 Employment lifecycle')}
      {openSection === 'employment' && (
        <div style={{ padding: '0.5rem 0.25rem', ...twoCol }}>
          <div style={inputWrap}>
            <label style={labelSm}>Start date</label>
            <input type="date" name="start_date" defaultValue={staff?.start_date?.slice(0, 10) || ''} style={inputStyle} data-testid="staff-start-date" />
          </div>
          <div style={inputWrap}>
            <label style={labelSm}>Probation end date</label>
            <input type="date" name="probation_end_date" defaultValue={staff?.probation_end_date?.slice(0, 10) || ''} style={inputStyle} data-testid="staff-probation-end" />
          </div>
          <div style={inputWrap}>
            <label style={labelSm}>Notice period (weeks)</label>
            <input type="number" min="0" max="52" name="notice_period_weeks" defaultValue={staff?.notice_period_weeks ?? ''} style={inputStyle} data-testid="staff-notice-weeks" />
          </div>
        </div>
      )}

      {/* ============================================================
          2. Job info
         ============================================================ */}
      {sectionHeader('job', '👔 Job info')}
      {openSection === 'job' && (
        <div style={{ padding: '0.5rem 0.25rem' }}>
          <div style={{ ...inputWrap, marginBottom: '0.6rem' }}>
            <label style={labelSm}>Job title</label>
            <input type="text" name="job_title" defaultValue={staff?.job_title || ''} placeholder="e.g. Nursery Practitioner Level 3" style={inputStyle} maxLength={80} data-testid="staff-job-title" />
          </div>
          <div style={twoCol}>
            <div style={inputWrap}>
              <label style={labelSm}>Line manager</label>
              <select name="line_manager_user_id" defaultValue={staff?.line_manager_user_id || ''} style={inputStyle} data-testid="staff-line-manager">
                <option value="">— None —</option>
                {users
                  .filter((u) => u._id !== staff?.user_id) // can't be your own manager
                  .filter((u) => ['admin', 'org_owner', 'org_admin', 'deputy_manager', 'room_leader'].includes(u.role))
                  .map((u) => (
                    <option key={u._id} value={u._id}>{u.full_name} ({u.role})</option>
                  ))}
              </select>
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Assigned room</label>
              <select name="assigned_room_id" defaultValue={staff?.assigned_room_id || ''} style={inputStyle} data-testid="staff-assigned-room">
                <option value="">— Unassigned —</option>
                {rooms.map((r) => (
                  <option key={r._id} value={r._id}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          3. HMRC onboarding
         ============================================================ */}
      {sectionHeader('hmrc', '🏛️ HMRC onboarding')}
      {openSection === 'hmrc' && (
        <div style={{ padding: '0.5rem 0.25rem' }}>
          <div style={twoCol}>
            <div style={inputWrap}>
              <label style={labelSm}>Date of birth</label>
              <input type="date" name="date_of_birth" defaultValue={staff?.date_of_birth?.slice(0, 10) || ''} style={inputStyle} data-testid="staff-dob" />
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Sex (HMRC)</label>
              <select name="sex" defaultValue={staff?.sex || ''} style={inputStyle} data-testid="staff-sex">
                <option value="">—</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Nationality</label>
              <input type="text" name="nationality" defaultValue={staff?.nationality || ''} placeholder="British" style={inputStyle} maxLength={40} data-testid="staff-nationality" />
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Payroll ID</label>
              <input type="text" name="payroll_id" defaultValue={staff?.payroll_id || ''} placeholder="EMP-…" style={inputStyle} maxLength={20} data-testid="staff-payroll-id" />
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Starter declaration</label>
              <select name="starter_declaration" defaultValue={staff?.starter_declaration || ''} style={inputStyle} data-testid="staff-starter-decl">
                <option value="">—</option>
                <option value="A">A — first job this tax year</option>
                <option value="B">B — only job now, worked earlier this year</option>
                <option value="C">C — has another job or pension</option>
              </select>
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Student loan plan</label>
              <select name="student_loan_plan" defaultValue={staff?.student_loan_plan || ''} style={inputStyle} data-testid="staff-student-loan">
                <option value="">—</option>
                <option value="none">None</option>
                <option value="plan_1">Plan 1</option>
                <option value="plan_2">Plan 2</option>
                <option value="plan_4">Plan 4 (Scotland)</option>
                <option value="plan_5">Plan 5</option>
                <option value="pgl">Postgraduate loan (PGL)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          4. Emergency contact (admin-managed)
         ============================================================ */}
      {sectionHeader('emergency', '🚨 Emergency contact')}
      {openSection === 'emergency' && (
        <div style={{ padding: '0.5rem 0.25rem' }}>
          <div style={twoCol}>
            <div style={inputWrap}>
              <label style={labelSm}>Contact name</label>
              <input type="text" name="emergency_contact_name" defaultValue={staff?.emergency_contact_name || ''} style={inputStyle} maxLength={80} data-testid="staff-ec-name" />
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Phone</label>
              <input type="tel" name="emergency_contact_phone" defaultValue={staff?.emergency_contact_phone || ''} style={inputStyle} maxLength={30} data-testid="staff-ec-phone" />
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Relationship</label>
              <input type="text" name="emergency_contact_relationship" defaultValue={staff?.emergency_contact_relationship || ''} placeholder="Partner / Sister / …" style={inputStyle} maxLength={40} data-testid="staff-ec-rel" />
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          5. Custom competencies
         ============================================================ */}
      {sectionHeader('competencies', `🏅 Custom competencies`, customCompetencies.length ? <span style={{ opacity: 0.6, fontWeight: 400 }}>({selectedCustom.length}/{customCompetencies.length})</span> : null)}
      {openSection === 'competencies' && (
        <div style={{ padding: '0.5rem 0.25rem' }}>
          {customCompetencies.length === 0 ? (
            <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
              No custom competencies defined yet for this organisation. Ask an admin to add some via
              {' '}<em>Settings → Custom Competencies</em>.
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.4rem' }}>
              {customCompetencies.map((c) => {
                const checked = selectedCustom.includes(c._id);
                return (
                  <label key={c._id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', padding: '0.35rem 0.5rem', borderRadius: 6, border: `1px solid ${checked ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`, background: checked ? 'hsl(var(--accent))' : 'transparent' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setSelectedCustom((cur) => (
                          e.target.checked
                            ? [...cur, c._id]
                            : cur.filter((id) => id !== c._id)
                        ));
                      }}
                      data-testid={`staff-custom-comp-${c._id}`}
                    />
                    <span>{c.icon || '•'} {c.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============================================================
          6. History timelines
         ============================================================ */}
      {(staff?.salary_history?.length || staff?.employment_events?.length) && (
        <>
          {sectionHeader('history', '📜 History')}
          {openSection === 'history' && (
            <div style={{ padding: '0.5rem 0.25rem', fontSize: '0.85rem' }}>
              {staff?.salary_history?.length ? (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Salary history</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }} data-testid="staff-salary-history">
                    {staff.salary_history
                      .slice()
                      .reverse()
                      .map((h, i) => (
                        <li key={i}>
                          <strong>£{h.old_rate ?? '—'} → £{h.new_rate}</strong>
                          {' · '}{h.effective_from ? new Date(h.effective_from).toLocaleDateString() : '—'}
                          {' · '}<span style={{ color: 'hsl(var(--muted-foreground))' }}>{h.changed_by_email || '—'}</span>
                          {h.reason ? <> · <em>{h.reason}</em></> : null}
                        </li>
                      ))}
                  </ul>
                </>
              ) : null}
              {staff?.employment_events?.length ? (
                <>
                  <div style={{ fontWeight: 600, margin: '0.5rem 0 4px' }}>Employment events</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }} data-testid="staff-employment-events">
                    {staff.employment_events
                      .slice()
                      .reverse()
                      .map((e, i) => (
                        <li key={i}>
                          <strong>{e.type === 'leaver' ? '👋 Leaver' : e.type === 'rehire' ? '↩️ Rehire' : e.type}</strong>
                          {' · '}{e.effective_date ? new Date(e.effective_date).toLocaleDateString() : '—'}
                          {e.leaver_reason ? <> · reason: <em>{e.leaver_reason}</em></> : null}
                          {e.leaver_reason_notes ? <> · {e.leaver_reason_notes}</> : null}
                          {e.actor_email ? <> · by <span style={{ color: 'hsl(var(--muted-foreground))' }}>{e.actor_email}</span></> : null}
                        </li>
                      ))}
                  </ul>
                </>
              ) : null}
            </div>
          )}
        </>
      )}

      {/* ============================================================
          7. Documents
         ============================================================ */}
      {staffId && sectionHeader('documents', '📎 HR documents', <span style={{ opacity: 0.6, fontWeight: 400 }}>({documents.length})</span>)}
      {staffId && openSection === 'documents' && (
        <div style={{ padding: '0.5rem 0.25rem' }}>
          {/* Upload row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.4rem', marginBottom: '0.5rem' }}>
            <div style={inputWrap}>
              <label style={labelSm}>Type</label>
              <select value={uploadForm.document_type} onChange={(e) => setUploadForm((f) => ({ ...f, document_type: e.target.value }))} style={inputStyle} data-testid="staff-doc-type">
                <option value="dbs">DBS certificate</option>
                <option value="passport">Passport / Right to work</option>
                <option value="right_to_work">Right to work (other)</option>
                <option value="contract">Signed contract</option>
                <option value="reference">Reference</option>
                <option value="qualification">Qualification certificate</option>
                <option value="first_aid_cert">First aid certificate</option>
                <option value="food_hygiene_cert">Food hygiene certificate</option>
                <option value="safeguarding_cert">Safeguarding certificate</option>
                <option value="cv">CV</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Issue date</label>
              <input type="date" value={uploadForm.issue_date} onChange={(e) => setUploadForm((f) => ({ ...f, issue_date: e.target.value }))} style={inputStyle} data-testid="staff-doc-issue" />
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>Expiry date</label>
              <input type="date" value={uploadForm.expiry_date} onChange={(e) => setUploadForm((f) => ({ ...f, expiry_date: e.target.value }))} style={inputStyle} data-testid="staff-doc-expiry" />
            </div>
            <div style={inputWrap}>
              <label style={labelSm}>File</label>
              <input type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} style={inputStyle} accept=".pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" data-testid="staff-doc-file" />
            </div>
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <input type="text" placeholder="Notes (optional)" value={uploadForm.notes} onChange={(e) => setUploadForm((f) => ({ ...f, notes: e.target.value }))} style={inputStyle} maxLength={200} data-testid="staff-doc-notes" />
          </div>
          <button type="button" className="btn btn-secondary" disabled={uploading || !uploadFile} onClick={handleUpload} data-testid="staff-doc-upload">
            {uploading ? 'Uploading…' : '📎 Upload document'}
          </button>

          {/* List */}
          <div style={{ marginTop: '0.75rem', border: '1px solid hsl(var(--border))', borderRadius: 6 }}>
            {docsLoading ? (
              <div style={{ padding: '0.6rem', color: 'hsl(var(--muted-foreground))' }}>Loading…</div>
            ) : documents.length === 0 ? (
              <div style={{ padding: '0.6rem', color: 'hsl(var(--muted-foreground))' }}>No documents yet.</div>
            ) : (
              documents.map((d) => {
                const expDate = d.expiry_date ? new Date(d.expiry_date) : null;
                const daysToExpiry = expDate ? Math.ceil((expDate - Date.now()) / 86400000) : null;
                const expBadge = daysToExpiry !== null
                  ? daysToExpiry < 0
                    ? { color: 'hsl(0 65% 40%)', label: `⚠️ Expired ${Math.abs(daysToExpiry)}d ago` }
                    : daysToExpiry <= 30
                      ? { color: 'hsl(30 80% 40%)', label: `⏰ Expires in ${daysToExpiry}d` }
                      : { color: 'hsl(142 45% 30%)', label: `✓ Expires ${expDate.toLocaleDateString()}` }
                  : null;
                return (
                  <div key={d._id} data-testid={`staff-doc-row-${d._id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.6rem', borderBottom: '1px solid hsl(var(--border))', fontSize: '0.85rem' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {d.document_type.replace(/_/g, ' ')}
                        {' · '}
                        <span style={{ fontWeight: 400, color: 'hsl(var(--muted-foreground))' }}>{d.original_filename}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                        {expBadge && <span style={{ color: expBadge.color, fontWeight: 600 }}>{expBadge.label}</span>}
                        {expBadge && ' · '}
                        Uploaded {new Date(d.created_at).toLocaleDateString()} by {d.uploaded_by_email || 'unknown'}
                        {d.notes ? <> · {d.notes}</> : null}
                      </div>
                    </div>
                    {d.download_url && (
                      <a
                        href={d.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost"
                        style={{ fontSize: '0.8rem', padding: '0.2rem 0.55rem' }}
                        data-testid={`staff-doc-download-${d._id}`}
                      >
                        ⬇ Download
                      </a>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ color: 'hsl(0 60% 40%)', fontSize: '0.8rem', padding: '0.2rem 0.55rem' }}
                      onClick={() => handleDeleteDoc(d._id, d.original_filename || 'this document')}
                      data-testid={`staff-doc-delete-${d._id}`}
                    >
                      🗑
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ============================================================
          8. Danger zone — Archive / Restore
         ============================================================ */}
      {staffId && sectionHeader('danger', '⚠️ Danger zone')}
      {staffId && openSection === 'danger' && (
        <div style={{ padding: '0.5rem 0.25rem' }}>
          {staff?.is_active === false ? (
            <>
              <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: 0 }}>
                This staff member was archived
                {staff.end_date ? ` on ${new Date(staff.end_date).toLocaleDateString()}` : ''}
                {staff.leaver_reason ? ` — reason: ${staff.leaver_reason}` : ''}.
                {staff.is_rehire_eligible === false ? ' They are marked NOT rehire-eligible.' : ''}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                disabled={staff.is_rehire_eligible === false}
                onClick={submitRestore}
                data-testid="staff-restore-btn"
              >
                ↩️ Restore / Rehire
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: 0 }}>
                Archiving marks this staff member as a leaver. Their login will be deactivated and they'll be excluded from rota, payroll, and ratio calculations. The record itself is preserved for auditing.
              </p>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ color: 'hsl(0 60% 40%)', borderColor: 'hsl(0 60% 60%)' }}
                onClick={() => setLeaverForm((f) => ({ ...f, open: true }))}
                data-testid="staff-archive-btn"
              >
                👋 Archive as leaver…
              </button>
            </>
          )}
        </div>
      )}

      {/* Leaver modal */}
      {leaverForm.open && (
        <div className="modal-overlay" onClick={() => leaverForm.submitting || setLeaverForm((f) => ({ ...f, open: false }))} data-testid="staff-leaver-modal">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h2 style={{ margin: 0 }}>👋 Archive as leaver</h2>
            </div>
            <div className="form-group">
              <label>Reason</label>
              <select
                value={leaverForm.leaver_reason}
                onChange={(e) => setLeaverForm((f) => ({ ...f, leaver_reason: e.target.value }))}
                data-testid="staff-leaver-reason"
              >
                <option value="resignation">Resignation</option>
                <option value="end_of_contract">End of contract</option>
                <option value="redundancy">Redundancy</option>
                <option value="dismissal">Dismissal</option>
                <option value="retirement">Retirement</option>
                <option value="mutual_agreement">Mutual agreement</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Last working day</label>
              <input
                type="date"
                value={leaverForm.last_working_day}
                onChange={(e) => setLeaverForm((f) => ({ ...f, last_working_day: e.target.value }))}
                required
                data-testid="staff-leaver-last-day"
              />
            </div>
            <div className="form-group">
              <label>Notes (optional)</label>
              <textarea
                rows={2}
                value={leaverForm.leaver_reason_notes}
                onChange={(e) => setLeaverForm((f) => ({ ...f, leaver_reason_notes: e.target.value }))}
                data-testid="staff-leaver-notes"
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={leaverForm.is_rehire_eligible}
                  onChange={(e) => setLeaverForm((f) => ({ ...f, is_rehire_eligible: e.target.checked }))}
                  data-testid="staff-leaver-rehire"
                />
                <span>Eligible for rehire</span>
              </label>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={leaverForm.submitting}
                onClick={() => setLeaverForm((f) => ({ ...f, open: false }))}
                data-testid="staff-leaver-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ background: 'hsl(0 60% 45%)' }}
                disabled={leaverForm.submitting || !leaverForm.last_working_day}
                onClick={submitArchive}
                data-testid="staff-leaver-submit"
              >
                {leaverForm.submitting ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
